import type { PopoverProps, Theme } from '@material-ui/core';
import { createStyles, Grid, Popover } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { i18n, Typography } from '@nutrien/cxp-components';
import React, { useEffect, useState } from 'react';
import { useRxCollection } from 'rxdb-hooks';

import { useCurrentBorer } from '@/rxdb/Equipment/useCurrentBorer';
import useInspections, { InspectionMenuOption } from '@/rxdb/Inspections/useInspections';
import RxdbCollectionName from '@/rxdb/rxdbCollectionName';
import { sortInspectionMenu } from '@/utilities/sortHelper';
import { formatAsStartCase } from '@/utilities/utilityFunctions';

import {
  EquipmentTypeName,
  InspectionTab,
  InspectionType,
  MiningMethod,
} from '../../utilities/constants';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    popover: {
      borderRadius: '8px !important',
    },
    div: { backgroundColor: theme.palette.background.default },
    container: {
      padding: '16px',
      backgroundColor: 'rgba(56,56,56,1)',
    },
    title: {
      paddingBottom: '24px',
      color: theme.palette.text.secondary,
    },
    link: {
      padding: '10px 0px',
    },
  }),
);

interface Props extends PopoverProps {
  openAddHazard: () => void;
  onAddCuttingPermit: (inspectionId: string) => void;
  onAddAreaCheck: (inspectionId: string) => void;
  onAddWorkAreaInspection: (inspectionId: string | undefined) => void;
  isCurrentShiftSelected: boolean;
}

const HazardMenu = ({
  open,
  anchorEl,
  onClose,
  openAddHazard,
  onAddCuttingPermit,
  onAddAreaCheck,
  isCurrentShiftSelected,
  onAddWorkAreaInspection,
}: Props) => {
  const classes = useStyles();
  const [inspectionMenuOptions, setInspectionMenuOptions] = useState<InspectionMenuOption[]>([]);
  const { borerInitialized, miningMethod, isRehabBorer } = useCurrentBorer();
  const { getInspectionsForMenu, inspectionsInitialized } = useInspections();
  const inspectionsCollection = useRxCollection(RxdbCollectionName.INSPECTIONS);

  const setUpMenu = async () => {
    const dynamicMenuOptions = await getInspectionsForMenu(
      InspectionTab.AREA,
      miningMethod as MiningMethod,
      isRehabBorer ? EquipmentTypeName.Rehab : EquipmentTypeName.Borer,
    );
    setInspectionMenuOptions(dynamicMenuOptions);
  };

  useEffect(() => {
    if (!inspectionsCollection || !borerInitialized || !inspectionsInitialized) return;
    const sub = inspectionsCollection.$.subscribe(() => {
      setUpMenu();
    });
    setUpMenu();
    return () => sub.unsubscribe();
  }, [borerInitialized, inspectionsInitialized, isRehabBorer, inspectionsCollection]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      className={classes.popover}
    >
      <div className={classes.div}>
        <Grid container className={classes.container} style={{ width: 450 }}>
          {isCurrentShiftSelected && (
            <Grid item container xs={8}>
              <Grid item xs={12} className={classes.title}>
                <Typography variant="h6">{i18n.t('Checklist')}</Typography>
              </Grid>
              {inspectionMenuOptions
                .sort(sortInspectionMenu)
                .map((menuOption: InspectionMenuOption) => (
                  <Grid
                    item
                    xs={12}
                    key={menuOption.inspectionId}
                    className={classes.link}
                    onClick={() => {
                      if (menuOption.mappedInspectionType === InspectionType.CUTTING_PERMIT) {
                        onAddCuttingPermit(menuOption.inspectionId);
                      } else if (
                        menuOption.mappedInspectionType === InspectionType.WORKFACE_AREA_INSPECTION
                      ) {
                        onAddWorkAreaInspection(menuOption.inspectionId);
                      } else if (
                        menuOption.mappedInspectionType === InspectionType.CUSTOM_AREA_CHECK
                      ) {
                        onAddAreaCheck(menuOption.inspectionId);
                      }
                    }}
                    id={`add-${menuOption.description}`}
                    data-testid={`${menuOption.inspectionId}`}
                  >
                    <Typography variant="body1">
                      {formatAsStartCase(menuOption.description)}
                    </Typography>
                  </Grid>
                ))}
            </Grid>
          )}
          <Grid item xs={4}>
            <Grid item xs={12} className={classes.title}>
              <Typography variant="h6">{i18n.t('Single entry')}</Typography>
            </Grid>
            <Grid
              item
              xs={12}
              className={classes.link}
              data-testid="add-hazard-option"
              onClick={openAddHazard}
              id="add-hazard"
            >
              <Typography variant="body1">{i18n.t('Hazard')}</Typography>
            </Grid>
          </Grid>
        </Grid>
      </div>
    </Popover>
  );
};

export default HazardMenu;
